// Phase T3.3 — public "STRIKZA Extraction Intelligence Record".
//
// No login. Aggregate-only. The CLV methodology applied to the
// product itself: judged by the same standard it uses to judge bets.
// SSR-safe (data fetched in a hook effect; honest empty state).

import { shapeExtractionStats } from "../../extractor/extractionStats.ts";
import { useExtractionStats } from "../../extractor/useExtractionStats.ts";

function Stat(
  { label, value }: { label: string; value: string },
): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "16px 18px",
        border: "1px solid var(--sa-hairline)",
        borderRadius: 6,
        background: "var(--sa-bg-raised)",
        minWidth: 160,
      }}
    >
      <span
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "var(--sa-fg-muted)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--sa-font-stat, var(--sa-font-display, system-ui))",
          fontSize: 26,
          color: "var(--sa-fg-primary)",
          fontWeight: 600,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Breakdown(
  { title, rows }: {
    title: string;
    rows: { key: string; n: number; avgClvPct: number }[];
  },
): JSX.Element {
  if (rows.length === 0) return <></>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--sa-fg-muted)",
        }}
      >
        {title}
      </span>
      {rows.map((r) => (
        <div
          key={r.key}
          data-testid={`ir-breakdown-${title.toLowerCase()}-${r.key}`}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            gap: 12,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-secondary)",
          }}
        >
          <span>{r.key}</span>
          <span style={{ color: "var(--sa-fg-muted)" }}>{r.n} legs</span>
          <span
            style={{
              color: r.avgClvPct >= 0
                ? "var(--sa-signal-green)"
                : "var(--sa-signal-red)",
            }}
          >
            {r.avgClvPct >= 0 ? "+" : ""}
            {r.avgClvPct.toFixed(2)}% CLV
          </span>
        </div>
      ))}
    </div>
  );
}

export function IntelligenceRecordPage(): JSX.Element {
  const { view, loading } = useExtractionStats();

  return (
    <main
      data-testid="intelligence-record"
      style={{
        maxWidth: 880,
        margin: "0 auto",
        padding: "48px 20px 80px",
        display: "flex",
        flexDirection: "column",
        gap: 28,
      }}
    >
      <header style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "var(--sa-accent-saffron)",
          }}
        >
          STRIKZA · Extraction Intelligence Record
        </span>
        <h1
          data-testid="ir-headline"
          style={{
            fontFamily: "var(--sa-font-display, system-ui)",
            fontSize: 24,
            lineHeight: 1.3,
            color: "var(--sa-fg-primary)",
            margin: 0,
            fontWeight: 600,
          }}
        >
          {view.headline}
        </h1>
        <p
          style={{
            fontFamily: "var(--sa-font-body)",
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          Closing Line Value, applied to the product itself. The same
          standard STRIKZA uses to judge a bet, it applies to judging
          its own intelligence. Aggregate only — no individual user
          data is ever exposed.
        </p>
      </header>

      {loading
        ? (
          <div
            data-testid="ir-loading"
            style={{ color: "var(--sa-fg-muted)", fontSize: 13 }}
          >
            Loading the record…
          </div>
        )
        : !view.hasRecord
        ? (
          <div
            data-testid="ir-empty"
            style={{
              padding: 18,
              border: "1px dashed var(--sa-hairline)",
              borderRadius: 6,
              fontFamily: "var(--sa-font-body)",
              fontSize: 13.5,
              color: "var(--sa-fg-secondary)",
              lineHeight: 1.6,
            }}
          >
            The record is accumulating. STRIKZA publishes the verifiable
            CLV figures here as soon as there is an honest sample — no
            placeholder numbers, no testimonials.
          </div>
        )
        : (
          <>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <Stat
                label="Extractions (all-time)"
                value={view.totalAllTime.toLocaleString()}
              />
              <Stat
                label="Last 30 days"
                value={view.total30d.toLocaleString()}
              />
              <Stat
                label="Positive CLV (30d)"
                value={view.positivePct30d === null
                  ? "accumulating"
                  : `${view.positivePct30d.toFixed(0)}%`}
              />
              <Stat
                label="Avg CLV vs original (30d)"
                value={view.avgClvPct30d === null
                  ? "accumulating"
                  : `${view.avgClvPct30d >= 0 ? "+" : ""}${view.avgClvPct30d.toFixed(2)}%`}
              />
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 32,
              }}
            >
              <Breakdown title="Sport" rows={view.sports} />
              <Breakdown title="Market" rows={view.markets} />
            </div>
          </>
        )}

      {view.generatedAt && (
        <footer
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10.5,
            color: "var(--sa-fg-muted)",
          }}
        >
          Generated {new Date(view.generatedAt).toUTCString()} · updated
          daily by the calibration cron · verify against your own
          extractions.
        </footer>
      )}
    </main>
  );
}

// Re-export for the marketing / PaywallCard embed.
export { shapeExtractionStats };
